import { BsThreeDotsVertical } from 'react-icons/bs';
import { useRouter } from 'next/router';
import { IoTriangle } from 'react-icons/io5';
import { IUserERC20Balance } from '@shared/types/frontend/assetPage';
import { CovalentBalanceItem } from '@shared/types/frontend';
import { toLocaleShortString, toRate, toSpecialFixed } from '@/lib/misc/utils';
import { getNetworkLogo, getTokenImageFromAssetItem } from '@/lib/misc/tokenImageHelper';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useSession } from '@/contexts/LitSessionProvider';
import { TAssetModes } from '../home/mobile/Dashboard/MobilePortfolio';
import TokenImage from '../shared/TokenImage';
import { Skeleton } from '../ui/skeleton';
import useAppDetails from '@/hooks/utilities/useAppDetails';
interface PortfolioAssetItemProps {
  assetMode: TAssetModes;
  assetItem: CovalentBalanceItem;
  assetData: IUserERC20Balance;
  setSelectedToken: (data: any) => void;
}

// DO NOT call useTokenData in this component - you get an insane number of requests that clog the network
export const PortfolioAssetItem = ({
  assetMode,
  assetItem,
  assetData,
  setSelectedToken
}: PortfolioAssetItemProps) => {
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    setMobileMode,
    setMobileModeMetadata
  } = useAppDetails();
  const router = useRouter();
  const rateChange = toRate(assetItem.quote_rate, assetItem.quote_rate_24h);
  const percentageAmount = rateChange / 100 * assetItem.usd_value;
  const portfolioAllocation = assetItem.usd_value / assetData.totalUsdValue * 100;
  function handleClick() {
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.portfolio.clickToken,
      category: CATEGORIES.portfolio,
      metaData: {
        tokenAddress: assetItem.contract_address,
        networkId: assetItem.network
      }
    });
  }
  const tokenImage = getTokenImageFromAssetItem(assetItem);
  const loading = !assetItem?.contract_name;
  return <div className='flex items-center w-full' data-sentry-component="PortfolioAssetItem" data-sentry-source-file="PortfolioAssetItem.tsx">
      <div className='w-full  flex items-center justify-between  min-h-[70px] pl-4 ' onClick={() => {
      handleClick();
      setMobileMode('asset');
      setMobileModeMetadata({
        networkId: assetItem.network,
        address: assetItem.contract_address,
        prev_m_mode: 'portfolio'
      });
      router.push(`/home?m_mode=asset&networkId=${assetItem.network}&address=${assetItem.contract_address}&prev_m_mode=portfolio`, undefined, {
        shallow: true
      });
    }}>
        <div className='w-5/12 cursor-pointer'>
          <div className='flex items-center'>
            {loading ? <div className='flex items-center '>
                <div className='relative'>
                  <Skeleton className='rounded-full h-8 w-8' />
                  <Skeleton className='absolute rounded-full h-4 w-4 -bottom-1 -right-1' />
                </div>

                <div className='ml-3 flex flex-col items-start whitespace-nowrap'>
                  <Skeleton className='w-24 h-4' />
                  <Skeleton className='w-16 h-3 mt-2' />
                </div>
              </div> : <>
                {/* <AutoTokenImage
                  tokenAddress={assetItem?.contract_address}
                  network={assetItem?.network}
                  size={32}
                  badgeSize={16}
                 /> */}
                <TokenImage mainImageSrc={tokenImage} badgeImageSrc={getNetworkLogo(assetItem?.network)} size={32} badgeSize={16} />
                <div className='ml-3 flex flex-col items-start whitespace-nowrap'>
                  <p className='text-base'>{toLocaleShortString(assetItem?.contract_name, 20)}</p>
                  <p className='text-xs text-gray-400'>
                    {toLocaleShortString(assetItem?.contract_ticker_symbol, 20)}
                  </p>
                </div>
              </>}
          </div>
        </div>
      </div>
      <div className={'flex flex-col w-3/4 pr-4 items-end justify-center  h-full min-h-[70px]'} onClick={() => {
      handleClick();
      setMobileMode('asset');
      setMobileModeMetadata({
        networkId: assetItem.network,
        address: assetItem.contract_address,
        prev_m_mode: 'portfolio'
      });
      router.push(`/home?m_mode=asset&networkId=${assetItem.network}&address=${assetItem.contract_address}&prev_m_mode=portfolio`, undefined, {
        shallow: true
      });
    }}>
        {loading ? <>
            <Skeleton className='w-16 h-4' />
            <Skeleton className='w-12 h-3 mt-2' />
          </> : <>
            <p className='text-base'>{`$${toSpecialFixed(assetItem.usd_value, 2)?.value}`}</p>
            {assetMode === 'Balance' && <p className='text-xs text-gray-400  text-right'>
                {`${toSpecialFixed(assetItem.formatted_balance, 2)?.value} ${toLocaleShortString(assetItem.contract_ticker_symbol, 3, true)}`}
              </p>}
            {assetMode === 'Returns (24hr)' && <div className='flex items-center'>
                {rateChange > 0 ? <>
                    <IoTriangle color='#34D399' size={12} />
                    <p className='text-xs text-green-400 ml-1  text-right'>
                      {`$${toSpecialFixed(percentageAmount, 2)?.value}`}
                    </p>
                  </> : <>
                    <IoTriangle color='#FF5B5B' size={12} className='rotate-180' />
                    <p className='text-xs text-red-400 ml-1  text-right'>
                      {`$${toSpecialFixed(Math.abs(percentageAmount), 2)?.value}`}
                    </p>
                  </>}
              </div>}
            {assetMode === 'Gains (24hr)' && <div className='flex items-center'>
                {rateChange > 0 ? <>
                    <IoTriangle color='#34D399' size={12} />
                    <p className='text-xs text-green-400 ml-1  text-right'>
                      {`${toSpecialFixed(rateChange, 2)?.value}%`}
                    </p>
                  </> : <>
                    <IoTriangle color='#FF5B5B' size={12} className='rotate-180' />
                    <p className='text-xs text-red-400 ml-1  text-right'>
                      {`${toSpecialFixed(Math.abs(rateChange), 2)?.value}%`}
                    </p>
                  </>}
              </div>}
            {assetMode === 'Allocation' && <div className='text-xs text-gray-400  text-right'>
                {toSpecialFixed(portfolioAllocation, 2).value}%
              </div>}
          </>}
      </div>
      <div className={'flex pr-4 flex-col w-2/12 items-end justify-center'}>
        <BsThreeDotsVertical size={24} onClick={() => {
        console.log('setting selected token', assetItem);
        setSelectedToken(assetItem);
      }} className='text-genius-cream/50' data-sentry-element="BsThreeDotsVertical" data-sentry-source-file="PortfolioAssetItem.tsx" />
      </div>
    </div>;
};